import {
  cosineSimilarity,
  relatedThresholdForSeed,
  variantThresholdForPool,
  variantThresholdForSeed,
} from './ranking';
import {
  UNCOVERED_BOUNDS,
  UNCOVERED_PERCENTILE,
  WEIGHT_AFFINITY_BOUNDS,
  WEIGHT_AFFINITY_PERCENTILE,
  adaptiveSimilarityThreshold,
} from './thresholds';
import type {
  Paper,
  PreferenceMapping,
  ProfileItem,
  RankingResult,
  ReviewDecision,
} from './types';

export type AuditedChoice = 'interested' | 'uncertain' | 'not_interested';
export type ChoiceSource = 'local' | 'imported';

export interface ChoiceAuditEntry {
  paper: Paper;
  choice?: AuditedChoice;
  source: ChoiceSource;
  importedValue?: string;
  baselineScore?: number;
  percentile?: number;
  expectedRange?: readonly [number, number];
  severity: number;
  alignmentStrength: number;
  outcome: 'consistent' | 'review' | 'unmapped';
  reason: string;
}

export interface ChoiceAlternative {
  paper: Paper;
  baselineScore: number;
  percentile: number;
  percentileImprovement: number;
  similarityToChoice?: number;
  matches: RankingResult['matches'];
}

export interface ChoiceAlternativeGroup {
  choice: ChoiceAuditEntry;
  alternatives: ChoiceAlternative[];
}

export interface ProfileWeightSuggestion {
  kind: 'increase_weight' | 'decrease_weight';
  profileItem: ProfileItem;
  suggestedWeight: number;
  positiveAffinity: number;
  negativeAffinity?: number;
  evidenceCount: number;
  reason: string;
}

export interface MissingAreaSuggestion {
  kind: 'add_area';
  label: string;
  text: string;
  paperIds: string[];
  representativeTitles: string[];
  reason: string;
}

export type ProfileSuggestion = ProfileWeightSuggestion | MissingAreaSuggestion;

export interface ChoiceAudit {
  confidence: 'ready' | 'descriptive';
  confidenceMessage: string;
  consistent: ChoiceAuditEntry[];
  review: ChoiceAuditEntry[];
  conflicts: Paper[];
  alternatives: ChoiceAlternativeGroup[];
  profileSuggestions: ProfileSuggestion[];
}

export interface ChoiceAuditInput {
  papers: readonly Paper[];
  rankings: readonly RankingResult[];
  decisions: Readonly<Record<string, ReviewDecision>>;
  preferenceMapping: PreferenceMapping;
  paperEmbeddings?: Readonly<Record<string, readonly number[]>>;
  profileItems?: readonly ProfileItem[];
  profileEmbeddings?: Readonly<Record<string, readonly number[]>>;
}

const EXPECTED_RANGES: Record<AuditedChoice, readonly [number, number]> = {
  not_interested: [0, 0.4],
  uncertain: [0.25, 0.75],
  interested: [0.55, 1],
};

function finiteBaseline(ranking: RankingResult | undefined): number | undefined {
  if (!ranking) return undefined;
  const score = ranking.denseScore ?? ranking.baseScore ?? ranking.score;
  return Number.isFinite(score) ? score : undefined;
}

function importedChoice(
  value: string,
  mapping: PreferenceMapping,
): AuditedChoice | 'unmapped' | undefined {
  const normalized = value.trim().toLocaleLowerCase().replace(/[_-]+/g, ' ');
  if (!normalized) return undefined;
  const labels: Record<string, AuditedChoice> = {
    interested: 'interested',
    uncertain: 'uncertain',
    'would review': 'uncertain',
    'would review if needed': 'uncertain',
    'not interested': 'not_interested',
  };
  if (labels[normalized]) return labels[normalized];

  const numeric = Number(normalized);
  if (!Number.isFinite(numeric)) return 'unmapped';
  const matches = (Object.entries(mapping) as Array<[AuditedChoice, number | null]>)
    .filter(([, mapped]) => mapped !== null && mapped === numeric)
    .map(([choice]) => choice);
  return matches.length === 1 ? matches[0] : 'unmapped';
}

interface ExtractedChoice {
  choice?: AuditedChoice;
  source: ChoiceSource;
  unmapped?: boolean;
}

function extractChoice(
  paper: Paper,
  decisions: Readonly<Record<string, ReviewDecision>>,
  mapping: PreferenceMapping,
): ExtractedChoice | undefined {
  const local = decisions[paper.id]?.status;
  if (local === 'interested' || local === 'uncertain' || local === 'not_interested') {
    return { choice: local, source: 'local' };
  }
  const imported = importedChoice(paper.preference, mapping);
  if (!imported) return undefined;
  if (imported === 'unmapped') return { source: 'imported', unmapped: true };
  return { choice: imported, source: 'imported' };
}

function percentile(score: number, scores: readonly number[]): number {
  let lower = 0;
  let tied = 0;
  for (const candidate of scores) {
    if (candidate < score) lower += 1;
    else if (candidate === score) tied += 1;
  }
  return (lower + tied * 0.5) / scores.length;
}

function distanceFromRange(value: number, range: readonly [number, number]): number {
  if (value < range[0]) return range[0] - value;
  if (value > range[1]) return value - range[1];
  return 0;
}

function strengthInsideRange(value: number, range: readonly [number, number]): number {
  if (value < range[0] || value > range[1]) return 0;
  if (range[0] === 0) return range[1] - value;
  if (range[1] === 1) return value - range[0];
  return Math.min(value - range[0], range[1] - value);
}

function entryReason(
  choice: AuditedChoice,
  value: number,
  range: readonly [number, number],
  aligned: boolean,
): string {
  const position = `${Math.round(value * 100)}th percentile`;
  if (aligned) return `This ${choice.replace('_', ' ')} choice is within its expected profile-fit range (${position}).`;
  if (value < range[0]) {
    return `This choice ranks lower against the current profile than expected (${position}).`;
  }
  return `This choice ranks higher against the current profile than expected (${position}).`;
}

function comparableVariation(scores: readonly number[]): boolean {
  if (scores.length < 6) return false;
  const minimum = Math.min(...scores);
  const maximum = Math.max(...scores);
  return maximum - minimum > 1e-6 && new Set(scores.map((score) => score.toFixed(6))).size >= 3;
}

function choiceEntries(input: ChoiceAuditInput): {
  entries: ChoiceAuditEntry[];
  conflicts: Paper[];
  scores: number[];
  rankByPaper: Map<string, RankingResult>;
} {
  const rankByPaper = new Map(input.rankings.map((ranking) => [ranking.paperId, ranking]));
  const scores = input.papers.flatMap((paper) => {
    const score = finiteBaseline(rankByPaper.get(paper.id));
    return score === undefined ? [] : [score];
  });
  const entries: ChoiceAuditEntry[] = [];
  const conflicts: Paper[] = [];

  for (const paper of input.papers) {
    if (input.decisions[paper.id]?.status === 'conflict') {
      conflicts.push(paper);
      continue;
    }
    const extracted = extractChoice(paper, input.decisions, input.preferenceMapping);
    if (!extracted) continue;
    const baselineScore = finiteBaseline(rankByPaper.get(paper.id));
    if (extracted.unmapped) {
      entries.push({
        paper,
        source: 'imported',
        importedValue: paper.preference,
        ...(baselineScore === undefined ? {} : { baselineScore }),
        severity: 1,
        alignmentStrength: 0,
        outcome: 'unmapped',
        reason: `Imported preference “${paper.preference}” does not map uniquely to a review choice.`,
      });
      continue;
    }

    const choice = extracted.choice!;
    const expectedRange = EXPECTED_RANGES[choice];
    if (baselineScore === undefined || scores.length === 0) {
      entries.push({
        paper,
        choice,
        source: extracted.source,
        ...(extracted.source === 'imported' ? { importedValue: paper.preference } : {}),
        expectedRange,
        severity: 1,
        alignmentStrength: 0,
        outcome: 'review',
        reason: 'No decision-independent profile score is available for this choice.',
      });
      continue;
    }
    const paperPercentile = percentile(baselineScore, scores);
    const severity = distanceFromRange(paperPercentile, expectedRange);
    entries.push({
      paper,
      choice,
      source: extracted.source,
      ...(extracted.source === 'imported' ? { importedValue: paper.preference } : {}),
      baselineScore,
      percentile: paperPercentile,
      expectedRange,
      severity,
      alignmentStrength: strengthInsideRange(paperPercentile, expectedRange),
      outcome: severity === 0 ? 'consistent' : 'review',
      reason: entryReason(choice, paperPercentile, expectedRange, severity === 0),
    });
  }

  return { entries, conflicts, scores, rankByPaper };
}

export const ALTERNATIVE_SIMILARITY_FLOOR = 0.5;
export const ALTERNATIVE_DIVERSITY_THRESHOLD = 0.92;
const MAX_ALTERNATIVES_PER_CHOICE = 3;

function alternativeReplacementScore(
  percentileImprovement: number,
  similarityToChoice: number | undefined,
): number {
  if (similarityToChoice === undefined) return percentileImprovement;
  return percentileImprovement * (0.2 + 0.8 * similarityToChoice);
}

function chooseAlternatives(
  input: ChoiceAuditInput,
  review: readonly ChoiceAuditEntry[],
  rankByPaper: ReadonlyMap<string, RankingResult>,
  scores: readonly number[],
): ChoiceAlternativeGroup[] {
  if (scores.length === 0) return [];
  const choicePaperIds = new Set(
    input.papers
      .filter((paper) => extractChoice(paper, input.decisions, input.preferenceMapping))
      .map((paper) => paper.id),
  );
  const conflicts = new Set(
    Object.entries(input.decisions)
      .filter(([, decision]) => decision.status === 'conflict')
      .map(([paperId]) => paperId),
  );
  const candidates = input.papers.flatMap((paper) => {
    if (choicePaperIds.has(paper.id) || conflicts.has(paper.id)) return [];
    const ranking = rankByPaper.get(paper.id);
    const baselineScore = finiteBaseline(ranking);
    if (baselineScore === undefined || !ranking) return [];
    return [{ paper, ranking, baselineScore, percentile: percentile(baselineScore, scores) }];
  });

  const claimed = new Set<string>();
  return review.flatMap((choice) => {
    if (
      (choice.choice !== 'interested' && choice.choice !== 'uncertain') ||
      choice.percentile === undefined ||
      choice.expectedRange === undefined ||
      choice.percentile >= choice.expectedRange[0]
    ) {
      return [];
    }
    const sourceEmbedding = input.paperEmbeddings?.[choice.paper.id];
    const paperEmbeddingMap = input.paperEmbeddings ?? {};
    const relatedFloor =
      sourceEmbedding && paperEmbeddingMap[choice.paper.id]
        ? relatedThresholdForSeed(choice.paper.id, sourceEmbedding, paperEmbeddingMap)
        : ALTERNATIVE_SIMILARITY_FLOOR;
    const diversityFloor =
      sourceEmbedding && paperEmbeddingMap[choice.paper.id]
        ? variantThresholdForSeed(choice.paper.id, sourceEmbedding, paperEmbeddingMap)
        : ALTERNATIVE_DIVERSITY_THRESHOLD;
    const choiceTopics = new Set(
      choice.paper.topics.map((topic) => topic.trim().toLocaleLowerCase()).filter(Boolean),
    );
    const ranked = candidates
      .filter((candidate) => candidate.percentile > choice.percentile!)
      .flatMap((candidate) => {
        const candidateEmbedding = input.paperEmbeddings?.[candidate.paper.id];
        const similarityToChoice =
          sourceEmbedding && candidateEmbedding
            ? cosineSimilarity(sourceEmbedding, candidateEmbedding)
            : undefined;
        if (similarityToChoice !== undefined && similarityToChoice < relatedFloor) {
          return [];
        }
        const percentileImprovement = candidate.percentile - choice.percentile!;
        return [{
          ...candidate,
          similarityToChoice,
          percentileImprovement,
          replacementScore: alternativeReplacementScore(percentileImprovement, similarityToChoice),
        }];
      })
      .sort(
        (left, right) =>
          right.replacementScore - left.replacementScore ||
          (right.similarityToChoice ?? -1) - (left.similarityToChoice ?? -1) ||
          left.paper.id.localeCompare(right.paper.id, undefined, { numeric: true }),
      );

    const selected: typeof ranked = [];
    const pick = (diversifyTopics: boolean): void => {
      for (const candidate of ranked) {
        if (selected.length === MAX_ALTERNATIVES_PER_CHOICE) break;
        if (claimed.has(candidate.paper.id) || selected.includes(candidate)) continue;
        const embedding = input.paperEmbeddings?.[candidate.paper.id];
        const duplicatesExisting =
          embedding &&
          selected.some((existing) => {
            const existingEmbedding = input.paperEmbeddings?.[existing.paper.id];
            return (
              existingEmbedding &&
              cosineSimilarity(embedding, existingEmbedding) >= diversityFloor
            );
          });
        if (duplicatesExisting) continue;
        if (diversifyTopics && selected.length > 0 && choiceTopics.size > 0) {
          const repeatsChoiceTopic = selected.some((existing) =>
            existing.paper.topics.some((topic) => {
              const key = topic.trim().toLocaleLowerCase();
              return (
                choiceTopics.has(key) &&
                candidate.paper.topics.some(
                  (candidateTopic) => candidateTopic.trim().toLocaleLowerCase() === key,
                )
              );
            }),
          );
          if (repeatsChoiceTopic) continue;
        }
        selected.push(candidate);
        claimed.add(candidate.paper.id);
      }
    };
    pick(true);
    if (selected.length < MAX_ALTERNATIVES_PER_CHOICE) pick(false);
    if (selected.length === 0) return [];
    return [{
      choice,
      alternatives: selected.map((candidate) => ({
        paper: candidate.paper,
        baselineScore: candidate.baselineScore,
        percentile: candidate.percentile,
        percentileImprovement: candidate.percentileImprovement,
        ...(candidate.similarityToChoice === undefined
          ? {}
          : { similarityToChoice: candidate.similarityToChoice }),
        matches: candidate.ranking.matches.slice(0, 3),
      })),
    }];
  });
}

function averageAffinity(
  paperIds: readonly { id: string; weight: number }[],
  paperEmbeddings: Readonly<Record<string, readonly number[]>>,
  profileEmbedding: readonly number[],
): { average: number; evidenceCount: number } {
  let total = 0;
  let totalWeight = 0;
  let evidenceCount = 0;
  for (const evidence of paperIds) {
    const embedding = paperEmbeddings[evidence.id];
    if (!embedding) continue;
    total += cosineSimilarity(profileEmbedding, embedding) * evidence.weight;
    totalWeight += evidence.weight;
    evidenceCount += 1;
  }
  return { average: totalWeight === 0 ? 0 : total / totalWeight, evidenceCount };
}

function sharedTopic(papers: readonly Paper[]): string | undefined {
  const counts = new Map<string, { label: string; count: number }>();
  for (const paper of papers) {
    for (const topic of paper.topics) {
      const key = topic.toLocaleLowerCase();
      const current = counts.get(key);
      counts.set(key, { label: topic, count: (current?.count ?? 0) + 1 });
    }
  }
  return [...counts.values()]
    .filter(({ count }) => count >= Math.max(2, Math.ceil(papers.length / 2)))
    .sort((left, right) => right.count - left.count || left.label.localeCompare(right.label))[0]?.label;
}

function missingAreaSuggestions(
  input: ChoiceAuditInput,
  positiveIds: readonly string[],
): MissingAreaSuggestion[] {
  const paperEmbeddings = input.paperEmbeddings ?? {};
  const profileEmbeddings = Object.values(input.profileEmbeddings ?? {});
  if (profileEmbeddings.length === 0) return [];
  const papersById = new Map(input.papers.map((paper) => [paper.id, paper]));
  const uncoveredFloor = adaptiveSimilarityThreshold(
    Object.values(paperEmbeddings).flatMap((embedding) => {
      const best = Math.max(
        ...profileEmbeddings.map((profile) => cosineSimilarity(profile, embedding)),
      );
      return Number.isFinite(best) ? [best] : [];
    }),
    UNCOVERED_PERCENTILE,
    0.35,
    UNCOVERED_BOUNDS,
  );
  const uncovered = positiveIds.filter((paperId) => {
    const embedding = paperEmbeddings[paperId];
    if (!embedding) return false;
    return Math.max(...profileEmbeddings.map((profile) => cosineSimilarity(profile, embedding))) <
      uncoveredFloor;
  });
  const clusterFloor = variantThresholdForPool(
    Object.fromEntries(
      uncovered.flatMap((id) => {
        const embedding = paperEmbeddings[id];
        return embedding ? [[id, embedding]] : [];
      }),
    ),
  );
  const remaining = new Set(uncovered);
  const groups: string[][] = [];
  while (remaining.size > 0) {
    const seed = remaining.values().next().value as string;
    remaining.delete(seed);
    const group = [seed];
    let expanded = true;
    while (expanded) {
      expanded = false;
      for (const candidate of [...remaining]) {
        const candidateEmbedding = paperEmbeddings[candidate];
        if (
          candidateEmbedding &&
          group.some((member) => {
            const memberEmbedding = paperEmbeddings[member];
            return (
              memberEmbedding &&
              cosineSimilarity(candidateEmbedding, memberEmbedding) >= clusterFloor
            );
          })
        ) {
          remaining.delete(candidate);
          group.push(candidate);
          expanded = true;
        }
      }
    }
    if (group.length >= 2) groups.push(group);
  }

  return groups.slice(0, 3).flatMap((paperIds) => {
    const papers = paperIds.flatMap((id) => {
      const paper = papersById.get(id);
      return paper ? [paper] : [];
    });
    if (papers.length < 2) return [];
    const topic = sharedTopic(papers);
    const representativeTitles = papers.slice(0, 3).map((paper) => paper.title);
    return [{
      kind: 'add_area' as const,
      label: topic ?? `Expertise represented by ${representativeTitles[0]}`,
      text: [
        topic ? `Research expertise related to ${topic}.` : 'Research expertise represented by:',
        ...representativeTitles.map((title) => `- ${title}`),
      ].join('\n'),
      paperIds,
      representativeTitles,
      reason: `${papers.length} positive choices form a related area that is weakly represented by the current profile.`,
    }];
  });
}

function profileSuggestions(input: ChoiceAuditInput): ProfileSuggestion[] {
  const profileItems = input.profileItems ?? [];
  const profileEmbeddings = input.profileEmbeddings ?? {};
  const paperEmbeddings = input.paperEmbeddings ?? {};
  const positive = Object.entries(input.decisions).flatMap(([id, decision]) => {
    if (decision.status === 'interested') return [{ id, weight: 1 }];
    if (decision.status === 'uncertain') return [{ id, weight: 0.5 }];
    return [];
  });
  const topicalNegative = Object.entries(input.decisions).flatMap(([id, decision]) =>
    decision.status === 'not_interested' && decision.penalizeSimilar ? [{ id, weight: 1 }] : [],
  );
  const suggestions: ProfileWeightSuggestion[] = [];

  for (const item of profileItems) {
    const embedding = profileEmbeddings[item.id];
    if (!embedding) continue;
    const positiveAffinity = averageAffinity(positive, paperEmbeddings, embedding);
    const negativeAffinity = averageAffinity(topicalNegative, paperEmbeddings, embedding);
    const affinityFloor = adaptiveSimilarityThreshold(
      Object.values(paperEmbeddings).map((paperEmbedding) =>
        cosineSimilarity(embedding, paperEmbedding),
      ),
      WEIGHT_AFFINITY_PERCENTILE,
      0.45,
      WEIGHT_AFFINITY_BOUNDS,
    );
    if (
      positiveAffinity.evidenceCount >= 2 &&
      positiveAffinity.average >= affinityFloor &&
      positiveAffinity.average - negativeAffinity.average >= 0.1 &&
      item.weight < 5
    ) {
      suggestions.push({
        kind: 'increase_weight',
        profileItem: item,
        suggestedWeight: Math.min(5, Math.round((item.weight + 0.5) * 10) / 10),
        positiveAffinity: positiveAffinity.average,
        ...(negativeAffinity.evidenceCount > 0
          ? { negativeAffinity: negativeAffinity.average }
          : {}),
        evidenceCount: positiveAffinity.evidenceCount,
        reason: `This profile item consistently matches ${positiveAffinity.evidenceCount} positive choices.`,
      });
    } else if (
      positiveAffinity.evidenceCount >= 3 &&
      negativeAffinity.evidenceCount >= 3 &&
      negativeAffinity.average - positiveAffinity.average >= 0.1 &&
      item.weight > 0.1
    ) {
      suggestions.push({
        kind: 'decrease_weight',
        profileItem: item,
        suggestedWeight: Math.max(0.1, Math.round((item.weight - 0.5) * 10) / 10),
        positiveAffinity: positiveAffinity.average,
        negativeAffinity: negativeAffinity.average,
        evidenceCount: positiveAffinity.evidenceCount + negativeAffinity.evidenceCount,
        reason: 'This item matches explicitly topical rejections more strongly than positive choices.',
      });
    }
  }

  return [
    ...suggestions.sort(
      (left, right) =>
        right.evidenceCount - left.evidenceCount ||
        left.profileItem.label.localeCompare(right.profileItem.label),
    ),
    ...missingAreaSuggestions(input, positive.map(({ id }) => id)),
  ];
}

export function auditChoices(input: ChoiceAuditInput): ChoiceAudit {
  const { entries, conflicts, scores, rankByPaper } = choiceEntries(input);
  const ready = comparableVariation(scores);
  const consistent = entries
    .filter((entry) => entry.outcome === 'consistent')
    .sort(
      (left, right) =>
        right.alignmentStrength - left.alignmentStrength ||
        left.paper.id.localeCompare(right.paper.id, undefined, { numeric: true }),
    );
  const review = entries
    .filter((entry) => entry.outcome !== 'consistent')
    .sort(
      (left, right) =>
        right.severity - left.severity ||
        left.paper.id.localeCompare(right.paper.id, undefined, { numeric: true }),
    );
  return {
    confidence: ready ? 'ready' : 'descriptive',
    confidenceMessage: ready
      ? 'Choices are compared with the distribution of current decision-independent profile scores.'
      : 'There are too few varied profile scores for strong grouping; treat these results as descriptive.',
    consistent,
    review,
    conflicts,
    alternatives: chooseAlternatives(input, review, rankByPaper, scores),
    profileSuggestions: profileSuggestions(input),
  };
}
